import React, { useCallback, useMemo } from 'react';
import { Card, CardContent, Typography, Box, Stack } from '@mui/material';

import type { PatientIntakeStep } from '@shared/patient-intake-status';

import { usePatientIntakeStatusContext } from '../../hooks';
import { determineIfStepIsDisabled, getStepStatus } from '../../utils';
import { useHistoryDialog, useHistoryDialogFilters } from '../../HistoryDialog';
import { StepIcon } from '../StepIcon.tsx';
import { StatusIcon } from '../StatusIcon.tsx';
import { DevSection } from './DevSection.tsx';
import { NoteSection } from './NoteSection.tsx';
import { ActionSection } from './ActionSection.tsx';
import { DescriptionSection } from './DescriptionSection.tsx';
import { StepDisabledBanner } from './StepDisabledBanner.tsx';
import { DEFAULT_STATUS_ACTIVITY } from './ActionPrompt.constants.ts';
import type { ActionPromptContextType } from './ActionPrompt.types.ts';
import { ActionPromptProvider, useActionPromptContext } from './ActionPromptContext.tsx';

type ActionPromptContentProps = {
  title: React.ReactNode;
  description: React.ReactNode;
  children?: React.ReactNode;
};

const ActionPromptContent = ({ title, description, children }: ActionPromptContentProps) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();
  const { onEdit, onClick, step, isDisabled, isViewOnlyMode } = useActionPromptContext();

  const handleCardClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    // If we are a healthcare provider, we can edit the step
    if (!isHealthcareProvider) {
      onClick();

      return;
    }

    if (isViewOnlyMode) {
      return;
    }

    onEdit?.();
  };

  // If the step is disabled and we are not a healthcare provider, we don't show it
  if (isDisabled && !isHealthcareProvider) {
    return null;
  }

  const status = getStepStatus(step);

  return (
    <>
      <Card
        variant="outlined"
        data-incompleted={status !== 'COMPLETED'}
        sx={{
          flex: 1,
          width: '100%',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s',

          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',

            '&:hover #update-status-mini-menu-item': {
              color: 'primary.main',
            },

            '&:hover:has(.mini-menu-item:hover) #update-status-mini-menu-item:not(:hover)': {
              color: 'inherit',
            },
          },
        }}
        onClick={handleCardClick}
      >
        <StepDisabledBanner />

        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" width="100%" gap={2}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
              <StatusIcon status={status} stepIdentifier={step.type} fontSize="medium" />
            </Box>
            <Box flex={1} alignSelf="flex-start" width="100%">
              <Typography variant="body1" component="div" display="flex" alignItems="center" gap={1} fontWeight="bold">
                <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', justifyContent: 'center' }}>
                  <StatusIcon status={status} stepIdentifier={step.type} sx={{ fontSize: '1rem' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'inherit' }}>
                  <StepIcon step={step} fontSize="inherit" />
                </Box>
                {title}
              </Typography>
              <DescriptionSection description={description} />
            </Box>

            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              justifyContent="flex-end"
              width={{ xs: '100%', sm: 'auto' }}
            >
              <ActionSection />
            </Stack>
          </Stack>

          <NoteSection />

          {children}

          <DevSection />
        </CardContent>
      </Card>
    </>
  );
};

type ActionPromptProps = Pick<
  ActionPromptContextType<PatientIntakeStep>,
  'step' | 'onClick' | 'onEdit' | 'onDelete' | 'onToggleDisabled'
> & {
  title: React.ReactNode;
  description: React.ReactNode;
  children?: React.ReactNode;
  isViewOnlyMode?: boolean;
};

export const ActionPrompt = ({
  step,
  title,
  children,
  description,
  isViewOnlyMode = false,
  onClick,
  onEdit,
  onDelete,
  onToggleDisabled,
}: ActionPromptProps) => {
  const { setIsOpen } = useHistoryDialog();
  const { setFilters } = useHistoryDialogFilters();

  const onShowHistory = useCallback(() => {
    setIsOpen(true);
    setFilters({ activeStep: { id: step.id, name: step.type } });
  }, [setIsOpen, setFilters, step]);

  const contextValue: ActionPromptContextType<PatientIntakeStep> = useMemo(
    () => ({
      onEdit,
      onClick,
      onDelete,
      onShowHistory,
      onToggleDisabled,
      step,
      isViewOnlyMode,
      isDisabled: determineIfStepIsDisabled({ step }),
      currentStatus:
        step.currentStatus ?? (DEFAULT_STATUS_ACTIVITY as NonNullable<NoInfer<PatientIntakeStep['currentStatus']>>),
    }),
    [step, onEdit, onClick, onDelete, onShowHistory, onToggleDisabled],
  );

  return (
    <ActionPromptProvider value={contextValue}>
      <ActionPromptContent title={title} description={description}>
        {children}
      </ActionPromptContent>
    </ActionPromptProvider>
  );
};
